import React, { useState, useEffect } from "react";
import { Table, UncontrolledDropdown, DropdownToggle, DropdownItem, DropdownMenu, Button } from "reactstrap";
import PropTypes from "prop-types";

import style from "../../Grid.module.scss";
import { getAccountType } from "../types";
import ModalConfirm from "../../Modal/ModalConfirm";
import ModalRelatedAccount from "../../Modal/ModalRelatedAccount";
import { CircularProgress } from "../../../..";

function generateEmptyRelatedAccount() {
  return {
    RelatedAccountId: "",
    RelatedAccountName: "",
    RelatedAccountNumber: "",
    CustodianEntityId: "",
    CustodianEntityName: "",
  };
}

const RelatedAccountsTab = ({ accountId, relatedAccounts, banks, isReadOnly, setAccountData, services }) => {
  const [fetching, setFetching] = useState(false);
  const [needAuth, setNeedAuth] = useState(false);

  useEffect(() => {
    services.getParameterValue({ ParameterName: "RelatedAccountChangesWithoutAuth" }, (response) => {
      if (response) {
        setNeedAuth(response.SdtParametersData.ParameterNumericValue == 0);
      }
    });
  }, [needAuth]);

  const [modalConfirm, setModalConfirm] = useState({
    open: false,
    title: "",
    saving: false,
    relatedAccountId: undefined,
  });
  const [modalRelatedAccount, setModalRelatedAccount] = useState({
    open: false,
    saving: false,
    relatedAccount: generateEmptyRelatedAccount(),
  });

  const toggleModalConfirm = () => {
    setModalConfirm({
      open: false,
      title: "",
      saving: false,
      relatedAccountId: "",
    });
  };

  const onChangeEdit = (name, value, strValue) => {
    setModalRelatedAccount({
      ...modalRelatedAccount,
      relatedAccount: {
        ...modalRelatedAccount.relatedAccount,
        [name]: value,
        CustodianEntityName:
          name === "CustodianEntityId" ? strValue : modalRelatedAccount.relatedAccount.CustodianEntityName,
      },
    });
  };

  const toggleModalRelatedAccount = (relatedAccount) => {
    setModalRelatedAccount({
      saving: modalRelatedAccount.saving,
      open: !modalRelatedAccount.open,
      relatedAccount:
        relatedAccount && relatedAccount.RelatedAccountId ? relatedAccount : generateEmptyRelatedAccount(),
    });
  };

  const handleEdit = (index) => {
    toggleModalRelatedAccount(relatedAccounts[index]);
  };

  const handleAdd = () => {
    toggleModalRelatedAccount();
  };

  const handleDelete = (index) => {
    const id = relatedAccounts[index].RelatedAccountId;
    const name = relatedAccounts[index].RelatedAccountName;
    setModalConfirm({
      open: true,
      title: "Are you sure you want to delete the selected Related Account?",
      saving: false,
      relatedAccountId: id,
      RelatedAccountName: name,
    });
  };

  const handleSaveRelatedAccount = () => {
    const newRelatedAccount = { ...modalRelatedAccount.relatedAccount };
    const account = {
      AccountId: accountId,
      RelatedAccount: [
        {
          ...newRelatedAccount,
          DeleteRelatedAccount: false,
        },
      ],
    };
    const body = {
      account,
      accountChangeType: getAccountType.RelatedAccounts,
    };
    setModalRelatedAccount({ ...modalRelatedAccount, saving: true });
    services.fetchAccountUpdate(body, (response) => {
      if (response && response.result && response.result.Code === 200) {
        let found = false;
        if (!newRelatedAccount.RelatedAccountId) {
          newRelatedAccount.RelatedAccountId = response.Id;
        }
        const newRelatedAccounts = relatedAccounts.map((relAcc) => {
          if (parseInt(newRelatedAccount.RelatedAccountId, 10) === parseInt(relAcc.RelatedAccountId, 10)) {
            found = true;
            return { ...newRelatedAccount };
          }
          return { ...relAcc };
        });
        if (!found) {
          newRelatedAccounts.push(newRelatedAccount);
        }
        account.RelatedAccount = newRelatedAccounts;
        setAccountData(account, true, needAuth);
        setModalRelatedAccount({
          saving: false,
          open: false,
          relatedAccount: generateEmptyRelatedAccount(),
        });
      } else {
        setModalRelatedAccount({
          ...modalRelatedAccount,
          saving: false,
        });
      }
    });
  };

  const deleteRelatedAccount = () => {
    const deleteAccountId = modalConfirm.relatedAccountId;
    const deleteAccountName = modalConfirm.RelatedAccountName;
    const account = {
      AccountId: accountId,
      RelatedAccount: [
        {
          RelatedAccountId: deleteAccountId,
          RelatedAccountName: deleteAccountName,
          DeleteRelatedAccount: true,
        },
      ],
    };
    const body = {
      account,
      accountChangeType: getAccountType.RelatedAccounts,
    };
    setModalConfirm({ ...modalConfirm, saving: true });
    services.fetchAccountUpdate(body, (response) => {
      if (response && response.result && response.result.Code === 200) {
        const newRelatedAccounts = [...relatedAccounts];
        let index = -1;
        newRelatedAccounts.forEach((relAcc, i) => {
          if (parseInt(deleteAccountId, 10) === parseInt(relAcc.RelatedAccountId, 10)) {
            index = i;
          }
        });
        if (index > -1) {
          newRelatedAccounts.splice(index, 1);
        }
        account.RelatedAccount = newRelatedAccounts;
        setAccountData(account, true, needAuth);
        toggleModalConfirm();
      } else {
        setModalConfirm({
          ...modalConfirm,
          saving: false,
        });
      }
    });
  };

  useEffect(() => {
    if (!relatedAccounts && !fetching) {
      const body = {
        AccountGetInfo: {
          GetAccountDataId: accountId,
          GetAccountDataType: getAccountType.RelatedAccounts,
        },
      };
      services.fetchAccountSectorInfo(body, (response) => {
        if (response && response.SDTAccount) {
          setAccountData(
            {
              AccountId: accountId,
              RelatedAccount: response.SDTAccount.RelatedAccount,
            },
            false
          );
        }
        setFetching(false);
      });
      setFetching(true);
    }
  }, [relatedAccounts, fetching, accountId, setAccountData, services]);

  return (
    <div>
      <Table className={style.table}>
        <thead>
          <tr>
            <th style={{ maxWidth: 50 }}> </th>
            <th style={{ maxWidth: 70 }}>#</th>
            <th>Name</th>
            <th>Custody</th>
          </tr>
        </thead>
        <tbody>
          {fetching ? (
            <tr>
              <td colSpan={5}>
                <CircularProgress sm />
              </td>
            </tr>
          ) : (
            <React.Fragment>
              {relatedAccounts && relatedAccounts.length > 0 ? (
                relatedAccounts.map((acc, i) => (
                  <tr key={acc.RelatedAccountId}>
                    <td style={{ width: 50 }}>
                      {!isReadOnly && (
                        <RenderDropdownActions index={i} onDelete={handleDelete} onEdit={handleEdit} />
                      )}
                    </td>
                    <td style={{ width: 70 }}>{acc.RelatedAccountNumber}</td>
                    <td>{acc.RelatedAccountName}</td>
                    <td>{acc.CustodianEntityName}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={4} style={{ textAlign: "center" }}>
                    No Results.
                  </td>
                </tr>
              )}
            </React.Fragment>
          )}
        </tbody>
      </Table>
      {!isReadOnly && (
        <Button color="primary" onClick={handleAdd}>
          <i className="fa fa-plus" /> Add Related Account
        </Button>
      )}
      <ModalConfirm {...modalConfirm} toggle={toggleModalConfirm} callback={deleteRelatedAccount} />
      <ModalRelatedAccount
        {...modalRelatedAccount}
        toggle={toggleModalRelatedAccount}
        onChange={onChangeEdit}
        onSave={handleSaveRelatedAccount}
        banks={banks}
      />
    </div>
  );
};

RelatedAccountsTab.propTypes = {
  accountId: PropTypes.string.isRequired,
  relatedAccounts: PropTypes.array.isRequired,
  setAccountData: PropTypes.func.isRequired,
  services: PropTypes.object.isRequired,
  banks: PropTypes.array,
};

RelatedAccountsTab.defaultProps = {
  banks: undefined,
};

function RenderDropdownActions({ onEdit, onDelete, index }) {
  return (
    <UncontrolledDropdown>
      <DropdownToggle color="link" size="lg" className={style.dropdown}>
        <i className="icon-options-vertical icons d-block" />
      </DropdownToggle>
      <DropdownMenu>
        <DropdownItem onClick={() => onEdit(index)}>
          <i className="fa fa-edit" />
          Edit
        </DropdownItem>
        <DropdownItem onClick={() => onDelete(index)}>
          <i className="fa fa-trash" />
          Delete
        </DropdownItem>
      </DropdownMenu>
    </UncontrolledDropdown>
  );
}

RenderDropdownActions.propTypes = {
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  index: PropTypes.number.isRequired,
};

export default RelatedAccountsTab;
